import React, { useEffect, useState } from "react";

// Define prop types using TypeScript
interface RadioInputProps {
  name: string;
  type?: string;
  label: string;
  errors?: any;
  errormessage?: string;
  placeholder?: string;
  defaultValue?: any;
  required?: boolean;
  onChange: (event: { target: { name: string; note: string; status: string; type: string } }) => void;
  value: { status: string | boolean; note: string };
  isDisable?: boolean;
}

const RadioInput: React.FC<RadioInputProps> = ({
  name,
  label,
  onChange,
  value,
  isDisable = false
}) => {
  const [status, setStatus] = useState<string>("false");
  const [note, setNote] = useState<string>("");

  useEffect(() => {
    setStatus(value?.status?.toString() || "false");
    setNote(value?.note?.toString() || "");
  }, [value]);

  const handleToggle = (newStatus: string) => {
    if (isDisable) return;
    setStatus(newStatus);
    if (newStatus === "false") setNote("");
    onChange({
      target: { name, note: newStatus === "true" ? note : "", status: newStatus, type: "radio" },
    });
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setNote(newValue);
    onChange({
      target: { name, note: newValue, status, type: "radio" },
    });
  };

  return (
    <div className="flex flex-col gap-2.5 mb-5">
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </label>
      <div className="flex items-center gap-5">
        {/* Slide toggle */}
        <div
          role="group"
          aria-label={`${label} - Yes or No`}
          className={`relative inline-flex p-0.5 rounded-lg border bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-600 ${
            isDisable ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {/* Sliding pill */}
          <div
            className={`absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded-md bg-white dark:bg-slate-700 shadow-sm border border-slate-200 dark:border-slate-600 transition-all duration-200 ease-out ${
              status === "true" ? "left-[calc(50%+2px)]" : "left-0.5"
            }`}
          />
          <button
            type="button"
            onClick={() => handleToggle("false")}
            disabled={isDisable}
            className={`relative z-10 w-14 py-1.5 text-[13px] font-medium rounded-md transition-colors duration-200 ${
              status === "false"
                ? "text-slate-800 dark:text-slate-100"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            } ${isDisable ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            No
          </button>
          <button
            type="button"
            onClick={() => handleToggle("true")}
            disabled={isDisable}
            className={`relative z-10 w-14 py-1.5 text-[13px] font-medium rounded-md transition-colors duration-200 ${
              status === "true"
                ? "text-slate-800 dark:text-slate-100"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            } ${isDisable ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            Yes
          </button>
        </div>

        {status === "true" && (
          <div className="flex-1 min-w-0">
            <input
              type="text"
              name="note"
              value={note}
              onChange={handleNoteChange}
              disabled={isDisable}
              placeholder="Add note..."
              className="w-full h-8 sm:h-9 px-3 rounded-md border border-indigo-500 dark:border-indigo-400 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default RadioInput;
